import * as MuiIcons from "@mui/icons-material";
import * as Mui from "@mui/material";
import {
  CellClassParams,
  ColGroupDef,
  ICellRendererParams,
} from "ag-grid-community";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";

import { nameof } from "../../common/ObjectUtils";
import {
  IARAggregatedRow,
  IARRowsPerGroup,
  ISingleLocationAvailabilityReport,
} from "../types/ISingleLocationAvailabilityReport";

import { useTranslation } from "react-i18next";
import "./availabilityReport.css";

type Props = {
  data: ISingleLocationAvailabilityReport;
};

const dateFormats = {
  tableGroupHeader: "DD/MM/YYYY HH:mm",
  tableSimpleHeader: "DD/MM/YYYY",
};

export const SingleLocationTable = (props: Props) => {
  const [t, i18n] = useTranslation(["operation"]);

  const [isAccordionExpanded, setIsAaccordionExpanded] = useState(true);

  const [totalPast, setTotalPast] = useState<IARAggregatedRow | undefined>();
  const [totalStartToday, setTotalStartToday] = useState<
    IARAggregatedRow | undefined
  >();
  const [totalCurrentToday, setTotalCurrentToday] = useState<
    IARAggregatedRow | undefined
  >();

  const formatUtilization = (value: any) => {
    const num = Number((value * 100).toFixed(2));
    return num.toLocaleString();
  }

  const totalRowsData = useMemo<IARRowsPerGroup[]>(
    () => [
      {
        group: `${t("availabilityReport.TOTALE")} - ${
          props.data.location.locationName
        }`,
        groupOrdering: 2000,
        isTotal: true,
        aggregatedRows: props.data.totalRowsByLocation,
      },
      {
        group: "",
        groupOrdering: 2001,
        aggregatedRows: [
          {
            dayType: "PAST",
            availability: t("availabilityReport.MAINTENANCE"),

            extraField: totalPast?.maintenance,
            startReservationNeed: t("availabilityReport.DEFLEET"),
            endReservationSupply: totalPast?.defleet,
          },
          {
            dayType: "START_TODAY",
            availability: t("availabilityReport.MAINTENANCE"),
            startReservationNeed: totalStartToday?.maintenance,
            endReservationSupply: t("availabilityReport.DEFLEET"),
            balance: totalStartToday?.defleet,
          },
          {
            dayType: "CURRENT_TODAY",
            availability: t("availabilityReport.MAINTENANCE"),
            startReservationNeed: totalCurrentToday?.maintenance,
            endReservationSupply: t("availabilityReport.DEFLEET"),
            balance: totalCurrentToday?.defleet,
          },
        ],
      },
      {
        group: "",
        groupOrdering: 2002,
        aggregatedRows: [
          {
            dayType: "PAST",
            availability: t("availabilityReport.trasfInEntrata"),
            extraField: totalPast?.incomingTranfers,
            startReservationNeed: t("availabilityReport.trasfInUscita"),
            endReservationSupply: totalPast?.outgoingTransfers,
          },
          {
            dayType: "START_TODAY",
            availability: t("availabilityReport.trasfInEntrata"),
            startReservationNeed: totalStartToday?.incomingTranfers,
            endReservationSupply: t("availabilityReport.trasfInUscita"),
            balance: totalStartToday?.outgoingTransfers,
          },
          {
            dayType: "CURRENT_TODAY",
            availability: t("availabilityReport.trasfInEntrata"),
            startReservationNeed: totalCurrentToday?.incomingTranfers,
            endReservationSupply: t("availabilityReport.trasfInUscita"),
            balance: totalCurrentToday?.outgoingTransfers,
          },
          ...props.data.totalRowsByLocation
            .filter((row) => row.dayType === "FUTURE")
            .map(
              (element): IARAggregatedRow => ({
                dayType: "FUTURE",
                endReservationSupply: t("availabilityReport.PREVRIENTRIREPL"),
                balance: element.replacementReturns,
                previewDate: element.previewDate,
              })
            ),
        ],
      },
      {
        group: "",
        groupOrdering: 2003,
        aggregatedRows: [
          {
            dayType: "PAST",
            availability: t("availabilityReport.RIENTRIREPL"),
            extraField: totalPast?.replacementReturns,
            startReservationNeed: t("availabilityReport.UTILIZZO"),
            endReservationSupply:
              totalPast && totalPast.utilization
                ? `${formatUtilization(totalPast?.utilization)} %`
                : t("availabilityReport.notAvailableAchronym"),
          },
          {
            dayType: "START_TODAY",
            availability: t("availabilityReport.PREVRIENTRIREPL"),
            startReservationNeed: totalStartToday?.replacementReturns,
            endReservationSupply: t("availabilityReport.UTILIZZO"),
            balance:
              totalStartToday && totalStartToday.utilization
                ? `${formatUtilization(totalStartToday?.utilization)} %`
                : t("availabilityReport.notAvailableAchronym"),
          },
          {
            dayType: "CURRENT_TODAY",
            availability: t("availabilityReport.RIENTRIREPL"),
            startReservationNeed: totalCurrentToday?.replacementReturns,
            endReservationSupply: t("availabilityReport.UTILIZZO"),
            balance:
              totalCurrentToday && totalCurrentToday.utilization
                ? `${formatUtilization(totalCurrentToday?.utilization)} %`
                : t("availabilityReport.notAvailableAchronym"),
          },
        ],
      },
    ],
    [totalPast, totalStartToday, totalCurrentToday, i18n.language]
  );

  const getDayName = (day: number) => {
    switch (day) {
      case 0:
        return t("availabilityReport.weekDays.sunday");

      case 1:
        return t("availabilityReport.weekDays.monday");

      case 2:
        return t("availabilityReport.weekDays.tuesday");

      case 3:
        return t("availabilityReport.weekDays.wednesday");

      case 4:
        return t("availabilityReport.weekDays.thursday");

      case 5:
        return t("availabilityReport.weekDays.friday");

      case 6:
        return t("availabilityReport.weekDays.saturday");

      default:
        return t("availabilityReport.weekDays.monday");
    }
  };

  const futureDaysColumnDefsData = useMemo<(string | undefined)[]>(
    () =>
      props.data.rowsPerGroup[0].aggregatedRows
        .filter((row) => row.dayType === "FUTURE")
        .map((row) => row.previewDate),
    []
  );

  const columnCellRenderer = (
    params: ICellRendererParams<IARRowsPerGroup>,
    value: string | number | undefined
  ) => {
    if (params.data?.isTotal || typeof value === "string") {
      return (
        <Mui.Tooltip title={value || ""}>
          <Mui.Typography variant="h6" my={"auto"}>
            {value}
          </Mui.Typography>
        </Mui.Tooltip>
      );
    }

    return value;
  };

  const columnDefs: ColGroupDef[] = [
    {
      headerName: "",
      children: [
        {
          headerName: "",
          minWidth: 200,
          field: nameof<IARRowsPerGroup>("group"),
          pinned: "left",
          cellClass: (params: CellClassParams<IARRowsPerGroup>) => {
            if (params.data?.isTotal) {
              return " ag-grid-cell-totals ";
            }
          },
          cellRenderer: (params: ICellRendererParams) => (
            <Mui.Tooltip title={params.value}>
              <Mui.Typography variant="h6" lineHeight={"inherit"}>
                {params.value}
              </Mui.Typography>
            </Mui.Tooltip>
          ),
        },
      ],
    },
    {
      headerName: `${t("availabilityReport.ieriFineGiornata")} ${dayjs()
        .subtract(1, "day")
        .set("hour", 23)
        .set("minute", 59)
        .format(dateFormats.tableGroupHeader)}`,
      headerClass: "ag-grid-cell-background-black",
      children: [
        {
          headerName: `${t("availabilityReport.rientratiIeri")} ${
            totalPast?.effectiveReturns || 0
          }`,
          headerClass: "ag-grid-cell-background-black",
          children: [
            {
              headerName: `${t("availabilityReport.raEmessiIeri")} ${
                totalPast?.issuedRa || 0
              }`,
              headerClass: "ag-grid-cell-background-black",
              children: [
                {
                  headerName: t("availabilityReport.AvailEndDay"),
                  minWidth: 200,
                  headerClass: "ag-grid-cell-background-black",
                  cellClass: (params: CellClassParams<IARRowsPerGroup>) => {
                    let ret =
                      "ag-grid-cell-background-black ag-grid-align-center";

                    if (params.data?.isTotal) {
                      ret += " ag-grid-cell-totals ";
                    }

                    return ret;
                  },
                  cellRenderer: (
                    params: ICellRendererParams<IARRowsPerGroup>
                  ) =>
                    columnCellRenderer(
                      params,
                      params.data?.aggregatedRows?.[0].availability
                    ),
                },
                {
                  headerName: "",
                  minWidth: 50,
                  headerClass: "ag-grid-cell-background-black",
                  cellClass: (params: CellClassParams<IARRowsPerGroup>) => {
                    let ret = "ag-grid-cell-background-black ag-grid-align-center";

                    if (params.data?.isTotal) {
                      ret += " ag-grid-cell-totals ";
                    }

                    return ret;
                  },
                  cellRenderer: (
                    params: ICellRendererParams<IARRowsPerGroup>
                  ) =>
                    columnCellRenderer(
                      params,
                      params.data?.aggregatedRows?.[0].extraField
                    ),
                },
              ],
            },
          ],
        },
        {
          headerName: `${t("availabilityReport.voidIeri")} ${
            totalPast?.voidCount || 0
          }`,
          headerClass: "ag-grid-cell-background-black",
          children: [
            {
              headerName: `${t("availabilityReport.failIeri")} ${
                totalPast?.failedCount || 0
              }`,
              headerClass: "ag-grid-cell-background-black",
              children: [
                {
                  headerName: t("availabilityReport.RES"),
                  minWidth: 100,
                  headerClass: "ag-grid-cell-background-black",
                  cellClass: (params: CellClassParams<IARRowsPerGroup>) => {
                    let ret = "ag-grid-cell-background-black ag-grid-align-center";

                    if (params.data?.isTotal) {
                      ret += " ag-grid-cell-totals ";
                    }

                    return ret;
                  },
                  cellRenderer: (
                    params: ICellRendererParams<IARRowsPerGroup>
                  ) =>
                    columnCellRenderer(
                      params,
                      params.data?.aggregatedRows?.[0].startReservationNeed
                    ),
                },
                {
                  headerName: t("availabilityReport.RIENTRI"),
                  minWidth: 200,
                  headerClass: "ag-grid-cell-background-black",
                  cellClass: (params: CellClassParams<IARRowsPerGroup>) => {
                    let ret = "ag-grid-cell-background-black ag-grid-align-center";

                    if (params.data?.isTotal) {
                      ret += " ag-grid-cell-totals ";
                    }

                    return ret;
                  },
                  cellRenderer: (
                    params: ICellRendererParams<IARRowsPerGroup>
                  ) =>
                    columnCellRenderer(
                      params,
                      params.data?.aggregatedRows?.[0].endReservationSupply
                    ),
                },
              ],
            },
          ],
        },
      ],
    },
    {
      headerName: `${t("availabilityReport.oggiInizioGiornata")} - ${dayjs()
        .set("hour", 7)
        .set("minute", 0)
        .format(dateFormats.tableGroupHeader)}`,
      headerClass: "ag-grid-cell-background-yellow",
      children: [
        {
          headerName: "",
          headerClass: "ag-grid-cell-background-yellow",
          children: [
            {
              headerName: "",
              headerClass: "ag-grid-cell-background-yellow",
              children: [
                {
                  headerName: t("availabilityReport.availStartDay"),
                  minWidth: 200,
                  headerClass: "ag-grid-cell-background-yellow",
                  cellClass: (params: CellClassParams<IARRowsPerGroup>) => {
                    let ret = "ag-grid-cell-background-yellow ag-grid-align-center";

                    if (params.data?.isTotal) {
                      ret += " ag-grid-cell-totals ";
                    }

                    return ret;
                  },
                  cellRenderer: (
                    params: ICellRendererParams<IARRowsPerGroup>
                  ) =>
                    columnCellRenderer(
                      params,
                      params.data?.aggregatedRows?.[1].availability
                    ),
                },
                {
                  headerName: t("availabilityReport.RES"),
                  minWidth: 100,
                  headerClass: "ag-grid-cell-background-yellow",
                  cellClass: (params: CellClassParams<IARRowsPerGroup>) => {
                    let ret = "ag-grid-cell-background-yellow ag-grid-align-center";

                    if (params.data?.isTotal) {
                      ret += " ag-grid-cell-totals ";
                    }

                    return ret;
                  },
                  cellRenderer: (
                    params: ICellRendererParams<IARRowsPerGroup>
                  ) =>
                    columnCellRenderer(
                      params,
                      params.data?.aggregatedRows?.[1].startReservationNeed
                    ),
                },
                {
                  headerName: t("availabilityReport.RIENTRI"),
                  minWidth: 200,
                  headerClass: "ag-grid-cell-background-yellow",
                  cellClass: (params: CellClassParams<IARRowsPerGroup>) => {
                    let ret = "ag-grid-cell-background-yellow ag-grid-align-center";

                    if (params.data?.isTotal) {
                      ret += " ag-grid-cell-totals ";
                    }

                    return ret;
                  },
                  cellRenderer: (
                    params: ICellRendererParams<IARRowsPerGroup>
                  ) =>
                    columnCellRenderer(
                      params,
                      params.data?.aggregatedRows?.[1].endReservationSupply
                    ),
                },
                {
                  headerName: t("availabilityReport.BAL"),
                  minWidth: 100,
                  headerClass: "ag-grid-cell-background-yellow",
                  cellClass: (params: CellClassParams<IARRowsPerGroup>) => {
                    let ret = "ag-grid-cell-background-yellow ag-grid-align-center";

                    if (params.data?.isTotal) {
                      ret += " ag-grid-cell-totals ";
                    }

                    return ret;
                  },
                  cellStyle: (params: CellClassParams<IARRowsPerGroup>) => {
                    const balance = params.data?.aggregatedRows?.[1]
                      .balance as number;
                    if ((balance && balance < 0) || balance === 0) {
                      return {
                        color: "red",
                      };
                    }
                  },
                  cellRenderer: (
                    params: ICellRendererParams<IARRowsPerGroup>
                  ) =>
                    columnCellRenderer(
                      params,
                      params.data?.aggregatedRows?.[1].balance
                    ),
                },
              ],
            },
          ],
        },
      ],
    },
    {
      headerName: `${t("availabilityReport.oggiTempoReale")} - ${dayjs().format(
        dateFormats.tableGroupHeader
      )}`,
      headerClass: "ag-grid-cell-background-green",
      children: [
        {
          headerName: `${t("availabilityReport.rientratiOggi")} ${
            totalCurrentToday?.effectiveReturns || 0
          }`,
          headerClass: "ag-grid-cell-background-green",
          children: [
            {
              headerName: `${t("availabilityReport.raEmessiOggi")} ${
                totalCurrentToday?.issuedRa || 0
              }`,
              headerClass: "ag-grid-cell-background-green",
              children: [
                {
                  headerName: "AVAIL NOW",
                  minWidth: 150,
                  headerClass: "ag-grid-cell-background-green",
                  cellClass: (params: CellClassParams<IARRowsPerGroup>) => {
                    let ret = "ag-grid-cell-background-green ag-grid-align-center";

                    if (params.data?.isTotal) {
                      ret += " ag-grid-cell-totals ";
                    }

                    return ret;
                  },
                  cellRenderer: (
                    params: ICellRendererParams<IARRowsPerGroup>
                  ) =>
                    columnCellRenderer(
                      params,
                      params.data?.aggregatedRows?.[2].availability
                    ),
                },
                {
                  headerName: t("availabilityReport.RES"),
                  minWidth: 100,
                  headerClass: "ag-grid-cell-background-green",
                  cellClass: (params: CellClassParams<IARRowsPerGroup>) => {
                    let ret = "ag-grid-cell-background-green ag-grid-align-center";

                    if (params.data?.isTotal) {
                      ret += " ag-grid-cell-totals ";
                    }

                    return ret;
                  },
                  cellRenderer: (
                    params: ICellRendererParams<IARRowsPerGroup>
                  ) =>
                    columnCellRenderer(
                      params,
                      params.data?.aggregatedRows?.[2].startReservationNeed
                    ),
                },
              ],
            },
          ],
        },
        {
          headerName: `${t("availabilityReport.voidOggi")} ${
            totalCurrentToday?.voidCount || 0
          }`,
          headerClass: "ag-grid-cell-background-green",
          children: [
            {
              headerName: `${t("availabilityReport.failOggi")} ${
                totalCurrentToday?.failedCount || 0
              }`,
              headerClass: "ag-grid-cell-background-green",
              children: [
                {
                  headerName: t("availabilityReport.RIENTRI"),
                  minWidth: 200,
                  headerClass: "ag-grid-cell-background-green",
                  cellClass: (params: CellClassParams<IARRowsPerGroup>) => {
                    let ret = "ag-grid-cell-background-green ag-grid-align-center";

                    if (params.data?.isTotal) {
                      ret += " ag-grid-cell-totals ";
                    }

                    return ret;
                  },
                  cellRenderer: (
                    params: ICellRendererParams<IARRowsPerGroup>
                  ) =>
                    columnCellRenderer(
                      params,
                      params.data?.aggregatedRows?.[2].endReservationSupply
                    ),
                },
                {
                  headerName: t("availabilityReport.BAL"),
                  minWidth: 100,
                  headerClass: "ag-grid-cell-background-green",
                  cellClass: (params: CellClassParams<IARRowsPerGroup>) => {
                    let ret = "ag-grid-cell-background-green ag-grid-align-center";

                    if (params.data?.isTotal) {
                      ret += " ag-grid-cell-totals ";
                    }

                    return ret;
                  },
                  cellStyle: (params: CellClassParams<IARRowsPerGroup>) => {
                    const balance = params.data?.aggregatedRows?.[2]
                      .balance as number;
                    if ((balance && balance < 0) || balance === 0) {
                      return {
                        color: "red",
                      };
                    }
                  },
                  cellRenderer: (
                    params: ICellRendererParams<IARRowsPerGroup>
                  ) =>
                    columnCellRenderer(
                      params,
                      params.data?.aggregatedRows?.[2].balance
                    ),
                },
              ],
            },
          ],
        },
      ],
    },
    ...futureDaysColumnDefsData.map(
      (element): ColGroupDef => ({
        headerName: "",
        children: [
          {
            headerGroupComponent: () => {
              const day = dayjs(element).day();
              let bgColor = "transparent";
              let textColor = "black";

              if (day === 6) {
                bgColor = "#00A0DF";
                textColor = "white";
              } else if (day === 0) {
                bgColor = "#F418A1";
                textColor = "white";
              }

              return (
                <Mui.Box
                  padding={2}
                  bgcolor={bgColor}
                  width={"100%"}
                  textAlign={"center"}
                  color={textColor}
                >
                  {getDayName(day)}
                </Mui.Box>
              );
            },
            headerClass: "ag-grid-cell-right-border ag-grid-cell-no-padding",
            children: [
              {
                headerName: dayjs(element).format(
                  dateFormats.tableSimpleHeader
                ),
                headerGroupComponent: () => (
                  <Mui.Box textAlign={"center"} width={"100%"}>
                    {dayjs(element).format(dateFormats.tableSimpleHeader)}
                  </Mui.Box>
                ),
                headerClass: "ag-grid-cell-right-border",
                children: [
                  {
                    headerName: t("availabilityReport.RES"),
                    minWidth: 100,
                    cellClass: (params: CellClassParams<IARRowsPerGroup>) => {
                      let ret = "ag-grid-align-center";

                      if (params.data?.isTotal) {
                        ret += " ag-grid-cell-totals ";
                      }

                      return ret;
                    },
                    cellRenderer: (
                      params: ICellRendererParams<IARRowsPerGroup>
                    ) =>
                      columnCellRenderer(
                        params,
                        params.data?.aggregatedRows.find(
                          (row) => row.previewDate === element
                        )?.startReservationNeed
                      ),
                  },
                  {
                    headerName: t("availabilityReport.RIENTRI"),
                    minWidth: 200,
                    cellClass: (params: CellClassParams<IARRowsPerGroup>) => {
                      let ret = "ag-grid-align-center";

                      if (params.data?.isTotal) {
                        ret += " ag-grid-cell-totals ";
                      }
                      
                      return ret;
                    },
                    cellRenderer: (
                      params: ICellRendererParams<IARRowsPerGroup>
                    ) =>
                      columnCellRenderer(
                        params,
                        params.data?.aggregatedRows.find(
                          (row) => row.previewDate === element
                        )?.endReservationSupply
                      ),
                  },
                  {
                    headerName: t("availabilityReport.BAL"),
                    minWidth: 100,
                    headerClass: "ag-grid-cell-right-border",
                    cellClass: (params: CellClassParams<IARRowsPerGroup>) => {
                      let ret = "ag-grid-align-center";

                      if (params.data?.isTotal) {
                        ret += " ag-grid-cell-totals ag-grid-cell-right-border ";
                      }
                      
                      return ret;
                    },
                    cellStyle: (params: CellClassParams<IARRowsPerGroup>) => {
                      const balance = params.data?.aggregatedRows.find(
                        (row) => row.previewDate === element
                      )?.balance as number;
                      if ((balance && balance < 0) || balance === 0) {
                        return {
                          color: "red",
                          borderRight: "solid 1px black",
                        };
                      }

                      return {
                        color: "inherit",
                        borderRight: "solid 1px black",
                      };
                    },
                    cellRenderer: (
                      params: ICellRendererParams<IARRowsPerGroup>
                    ) =>
                      columnCellRenderer(
                        params,
                        params.data?.aggregatedRows.find(
                          (row) => row.previewDate === element
                        )?.balance
                      ),
                  },
                ],
              },
            ],
          },
        ],
      })
    ),
  ];

  useEffect(() => {
    const past = props.data.totalRowsByLocation.find(
      (element) => element.dayType === "PAST"
    );
    const startToday = props.data.totalRowsByLocation.find(
      (element) => element.dayType === "START_TODAY"
    );
    const currentToday = props.data.totalRowsByLocation.find(
      (element) => element.dayType === "CURRENT_TODAY"
    );

    console.log(startToday);

    setTotalPast(past);
    setTotalStartToday(startToday);
    setTotalCurrentToday(currentToday);
  }, [props.data]);

  const allData = useMemo(() => {
    return [...props.data.rowsPerGroup, ...totalRowsData];
  }, [totalPast, totalCurrentToday, totalStartToday, i18n.language]);

  return (
    <Mui.Accordion expanded={isAccordionExpanded}>
      <Mui.AccordionSummary
        expandIcon={
          isAccordionExpanded ? (
            <MuiIcons.Remove />
          ) : (
            <MuiIcons.Add color={"error"} />
          )
        }
        onClick={() => setIsAaccordionExpanded(!isAccordionExpanded)}
      >
        <Mui.Stack direction={"row"} spacing={1} alignItems={"center"}>
          <Mui.Typography color={"#FF00FF"} variant="bodyLarge">
            {props.data.location.region}
          </Mui.Typography>
          <span>{` - `}</span>
          <Mui.Typography>{props.data.location.locationName}</Mui.Typography>
        </Mui.Stack>
      </Mui.AccordionSummary>

      <Mui.AccordionDetails>
        <AgGridCustomTable
          columnDefs={columnDefs}
          pagination={false}
          rowData={allData}
        />
      </Mui.AccordionDetails>
    </Mui.Accordion>
  );
};
